import React, { Fragment, useCallback, useEffect, useState } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ValidationSchema } from "../../../../conf/stations";
import { Loading } from "../../../../components/common";
import { useDispatch, useSelector } from "react-redux";
import Request from "../../../../core/requests";
import { openSnackbar } from "store/slices/snackbar";
import SideDialogComponent from "components/dialog/SideDialog.component";
import StationDetailForm from "forms/stations/StationDetailForm.component";
import { refreshToken } from "store/user.store";
import { logout } from "store/user.store";
import { useTranslation } from "react-i18next";

const SUPERUSER = "superuser";
const OPERATOR = "operator";

const UpdateStationContainer = ({
  station,
  open,
  setUpdate = () => { },
  handleCloseDialog = () => { },
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch();
  const {
    token,
    user,
    error: errorRefresh,
    loading: loadingRefresh,
  } = useSelector((state) => state.user);
  const [stationEdit, setStationEdit] = useState(null);
  const [isFirstRefresh, setIsFirstRefresh] = useState(true);
  const [loading, setLoading] = useState(false);
  const [dataSubmit, setDataSubmit] = useState(null);

  const { handleSubmit, control, formState, setValue } = useForm({
    resolver: yupResolver(ValidationSchema),
    defaultValues: {
      // id: '',
      tag: "",
      address: "",
      alias: "",
      coordinates: {},
      cluster: "",
      connectivityType: "",
      envMonitoring: false,
      envMonitoringType: "",
      camera: false,
      cameraAddress: "",
      wifi: false,
      wifiAddress: "",
      description: "",
      peopleCounter: false,
      gateway: false,
      gatewayAddress: "",
      gatewayAddressInternal: "",
      gatewayMAC: "",
      gatewayID: "",
      gatewaySerial: "",
    },
  });

  const updateForm = useCallback(() => {
    if (stationEdit) {
      // setValue('id', station?.stationId);
      setValue("tag", stationEdit?.tag);
      setValue("alias", stationEdit?.alias);
      setValue("address", stationEdit?.address);

      setValue("lat", stationEdit?.coordinates?.lat);
      setValue("long", stationEdit?.coordinates?.long);

      setValue("gateway", stationEdit?.gateway);
      setValue("gatewayAddress", stationEdit?.gatewayAddress);
      setValue("gatewayAddressInternal", stationEdit?.gatewayAddressInternal);
      setValue("gatewayMAC", stationEdit?.gatewayMAC);
      setValue("gatewayID", stationEdit?.gatewayID);
      setValue("gatewaySerial", stationEdit?.gatewaySerial);

      setValue("cluster", stationEdit?.cluster);
      setValue("connectivityType", stationEdit?.connectivityType);
      setValue("envMonitoring", stationEdit?.envMonitoring);
      setValue("envMonitoringType", stationEdit?.envMonitoringType);
      setValue("camera", stationEdit?.camera);
      setValue("cameraAddress", stationEdit?.cameraAddress);
      setValue("wifi", stationEdit?.wifi);
      setValue("wifiAddress", stationEdit?.wifiAddress);
      setValue("peopleCounter", stationEdit?.peopleCounter);
      setValue("description", stationEdit?.description);
    }
    // eslint-disable-next-line
  }, [stationEdit]);

  const onFailure = useCallback((error) => {
    setLoading(false);
    dispatch(
      openSnackbar({
        open: true,
        message: error.response?.data?.message ? error.response.data.message : "Errore",

        variant: "alert",
        alert: {
          color: "error",
        },
        close: false,
      })
    );
    // eslint-disable-next-line
  }, []);

  const onSubmit = useCallback(
    (data) => {

      if (user?.role === SUPERUSER || user?.role === OPERATOR) {
        const values = data;

        if (data?.lat) {
          values.coordinates.lat = data?.lat;
          delete values.lat;
        }

        if (data?.long) {
          values.coordinates.long = data?.long;
          delete values.long;
        }

        if (!values.envMonitoring) delete values.envMonitoringType;
        if (!values.camera) values.cameraAddress = null;
        if (!values.wifi) values.wifiAddress = null;

        setLoading(true);
        Request.stations
          .updateStationDetail(
            { id: stationEdit.stationId, token: token },
            { ...values }
          )
          .then((resp) => {
            onSuccess(resp);
          })
          .catch((error) => onError(error, data));
      }
    },
    // eslint-disable-next-line
    [token, stationEdit]
  );

  const onLogout = useCallback((error) => {
    if (error?.response?.status === 403 || error?.response?.status === 401) {
      dispatch(logout());
    }
    // eslint-disable-next-line
  }, []);

  const onError = useCallback(
    (error, data) => {
      setLoading(false);
      setDataSubmit(data);
      if (isFirstRefresh) {
        setLoading(false);
        if (error?.response?.status === 403) {
          dispatch(logout());
        } else {
          if (error?.response?.status !== 401) {
            onFailure(error);
          } else {
            if (isFirstRefresh) {
              dispatch(
                refreshToken({
                  grant_type: "refresh_token",
                  refresh_token: user.refresh_token,
                })
              );
              setIsFirstRefresh(false);
            }
          }
        }
      } else {
        onLogout(error);
      }
    },
    // eslint-disable-next-line
    []
  );

  const onSuccess = useCallback((resp) => {
    setUpdate(resp);
    setIsFirstRefresh(true)
    dispatch(
      openSnackbar({
        open: true,
        message: resp.message,
        variant: "alert",
        alert: {
          color: resp.status,
        },
        close: false,
      })
    );
    setLoading(false);
    handleCloseDialog();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setUpdate(null)
    if (station?.stationId) {
      setLoading(true);
      Request.stations
        .getStation({ station_id: station.stationId, token: token })
        .then((resp) => {
          setStationEdit(resp);
          setLoading(false);
          setIsFirstRefresh(true)
        })
        .catch((error) => onError(error, ""));
    }
    // eslint-disable-next-line
  }, [station, token]);

  useEffect(() => {
    if (dataSubmit && !isFirstRefresh && !errorRefresh && !loadingRefresh) {
      onSubmit(dataSubmit);
    }
    // eslint-disable-next-line
  }, [isFirstRefresh, loadingRefresh, errorRefresh, dataSubmit]);

  useEffect(() => {
    updateForm();
    // eslint-disable-next-line
  }, [stationEdit]);

  return (
    <Fragment>
      {loading && <Loading />}
      <SideDialogComponent open={open} handleCloseDialog={handleCloseDialog}>
        <StationDetailForm
          options={options}
          errors={formState.errors}
          control={control}
          // isSubmitting={isSubmitting}
          handleSubmit={handleSubmit(onSubmit)}
          labelButton={t('Edit')}
          title={`${t('Modify data')} ${station?.stationId}`}
          handleCloseDialog={handleCloseDialog}
          station={station}
          isAdd={false}
        />
      </SideDialogComponent>
    </Fragment>
  );
};

export default UpdateStationContainer;

const options = [
  { label: "Base", value: "base" },
  { label: "Advanced", value: "advanced" },
];
